// Destination Country Step

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image
} from "react-native";
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import SearchIcon from "../../../../../../../../assets/images/searchIcon.png";
import RadioSelectedIcon from "../../../../../../../../assets/images/Radio-Select-Button.png"


type ApiCountry = {
  id?: number;
  CountryCode: string;
  CountryName: string;
  IsActive: number | boolean;
};

type ChooseCountryStepProps = {
  onCountrySelected?: (code: string) => void;
};

export default function ChooseCountryStep({ onCountrySelected }: ChooseCountryStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [countries, setCountries] = useState<ApiCountry[]>([]);
  const setSelectedDestinationCountryCode = useSessionStore((s: SessionState) => s.setSelectedDestinationCountryCode);
  const setSelectedDestinationCountryName = useSessionStore((s: SessionState) => s.setSelectedDestinationCountryName);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await loadCountries();
        const apiCountries: ApiCountry[] = (result?.data ?? []).filter((c: ApiCountry) => {
          const isActive = typeof c.IsActive === 'number' ? c.IsActive === 1 : !!c.IsActive;
          return isActive && !!c.CountryName && !!c.CountryCode;
        });
        setCountries(apiCountries);
      } catch (e) {
        console.error('Failed to load countries in ChooseCountryStep', e);
      }
    };
    fetchCountries();
  }, []);

  // Filter countries by search
  const filteredCountries = useMemo(() => {
    const q = search.toLowerCase();
    return countries.filter((c) => (c.CountryName || '').toLowerCase().includes(q));
  }, [countries, search]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("COUNTRY_STEP_TITLE")}</Text>
      <Text style={styles.subtitle}>
        {t("COUNTRY_STEP_SUBTITLE")}
      </Text>

      {/* Search bar */}
      <View style={styles.searchContainer}>
        <Image
          source={SearchIcon}
          style={{height: 24, width: 24, marginRight: 8}}
          resizeMode='contain'
        />
        <TextInput
          style={styles.searchInput}
          placeholder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      {/* Country list */}
      <FlatList
        data={filteredCountries}
        keyExtractor={(item) => item.CountryCode}
        style={{ flex: 1, width: "100%" }} 
        renderItem={({ item }) => (
          <TouchableOpacity
            style={styles.countryRow}
            onPress={() => {
              setSelectedCountry(item.CountryName);
              setSelectedDestinationCountryName(item.CountryName);
              setSelectedDestinationCountryCode(item.CountryCode);
              onCountrySelected?.(item.CountryCode);
            }}
          >
            <Text style={styles.countryName}>{item.CountryName}</Text>
            {/* <View
              style={[
                styles.radioOuter,
                selectedCountry === item.CountryName && styles.radioOuterSelected,
              ]}
            >
              {selectedCountry === item.CountryName && <View style={styles.radioInner} />}
            </View> */}

            <View style={styles.radioButton}>
              {selectedCountry === item.CountryName && (
                <Image
                  source={RadioSelectedIcon}
                  style={{ height: 20, width: 20}}   
                  resizeMode='contain'     
                />
              )}
            </View>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 34,
    marginBottom: 8,
    color: Colors.titleTextColor,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 24,
    marginBottom: 19,
    color: Colors.subTitleColor,
  },
  searchContainer: {
    flexDirection: 'row',
    backgroundColor: Colors.searchBackground,
    padding: 8,
    borderRadius: 24,
    marginBottom: 19,
  },
  searchInput: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 24,
    color: Colors.subTitleColor,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: 'space-between'
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.darkGrey,
    paddingLeft: 16
  },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: '#D1D1D1'
  },
  radioOuterSelected: {
    borderColor: "#000",
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#000",
  },
  selectedText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: "500",
    color: "#111827",
  },
});
